import {
    createFileRoute,
    Outlet,
    useNavigate,
    useRouterState,
} from '@tanstack/react-router';

import { LIST_PAGE_FRAME_CLASSNAME } from '@apps/web/components/layout/page_layout';
import { ListPageHeader } from '@apps/web/components/ui/list_page_header';
import { ListPageTabs } from '@apps/web/components/ui/list_page_tabs';
import { Tabs, TabsList, TabsTrigger } from '@apps/web/components/ui/tabs';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import { cn } from '@apps/web/lib/utils';
import { useOrganization } from '@apps/web/swr_data_fetching/data_fetching_hooks';

export const Route = createFileRoute('/_app/(main)/training')({
    component: TrainingLayout,
    head: () => ({
        meta: [{ title: 'Staff Training | Phoebe' }],
    }),
});

const TRAINING_TABS = [
    { id: 'announcement', label: 'Announce Phoebe' },
    { id: 'onboarding', label: 'Onboard Caregivers' },
    { id: 'demo', label: 'Demo Page' },
];

function TrainingLayout() {
    const navigate = useNavigate();
    const organizationId = useOrganizationId();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'staff-training-layout',
    });
    const uiV3ComponentsEnabled = useFeatureFlag(
        'ui_v3_components',
        organization?.features,
    );
    const pathname = useRouterState({
        select: (state) => state.location.pathname,
    });

    const activeTab = pathname.endsWith('/onboarding')
        ? 'onboarding'
        : pathname.endsWith('/demo')
          ? 'demo'
          : 'announcement';
    const handleTabChange = (value: string) => {
        void navigate({
            to:
                value === 'onboarding'
                    ? '/training/onboarding'
                    : value === 'demo'
                      ? '/training/demo'
                      : '/training/announcement',
            replace: true,
        });
    };

    return (
        <div
            className={cn(
                'flex h-full min-w-0 flex-col overflow-hidden',
                uiV3ComponentsEnabled &&
                    cn(LIST_PAGE_FRAME_CLASSNAME, 'mx-auto w-full gap-5'),
            )}
        >
            {/* Header */}
            {uiV3ComponentsEnabled ? (
                <ListPageHeader
                    title="Staff Training"
                    description="Resources to help staff work effectively with Phoebe"
                />
            ) : (
                <div className="flex items-center justify-between px-4 py-4 sm:px-6">
                    <div className="min-w-0">
                        <h1 className="mb-1 text-xl font-semibold sm:text-2xl">
                            Staff Training
                        </h1>
                        <p className="text-muted-foreground text-sm">
                            Resources to help staff work effectively with Phoebe
                        </p>
                    </div>
                </div>
            )}
            {/* Content Area */}
            <div
                className={cn(
                    'min-w-0 flex-1 overflow-y-auto p-4 sm:p-6',
                    uiV3ComponentsEnabled && 'p-0 sm:p-0',
                )}
            >
                <div
                    className={cn(
                        'mx-auto w-full max-w-5xl min-w-0 space-y-6',
                        uiV3ComponentsEnabled && 'max-w-none',
                    )}
                >
                    {uiV3ComponentsEnabled ? (
                        <ListPageTabs
                            aria-label="Staff training resources"
                            tabs={TRAINING_TABS}
                            activeTab={activeTab}
                            onTabChange={handleTabChange}
                        />
                    ) : (
                        <Tabs value={activeTab} onValueChange={handleTabChange}>
                            <TabsList>
                                {TRAINING_TABS.map((tab) => (
                                    <TabsTrigger key={tab.id} value={tab.id}>
                                        {tab.label}
                                    </TabsTrigger>
                                ))}
                            </TabsList>
                        </Tabs>
                    )}
                    <Outlet />
                </div>
            </div>
        </div>
    );
}
